import { useCallback, useEffect, useId, useRef, useState } from "react";
import { BrandMark } from "../ui/BrandMark";
import { useSession } from "../lib/useSession";
import { PROTEST_LIVE, VERTICAL_LIVE, statusWord } from "../verticalStatus";
import { RequestAccessLink } from "../ui/RequestAccessLink";

/**
 * Static landing sections — ported from `valubrieflanding_2.html`'s markup/copy, scoped under
 * `.vb-landing` (see landing.css). No client-only APIs live here; `HeroCanvas` is the one island.
 *
 * TEXAS-WIDE REFRAME (session, 2026-07-26, per explicit owner instruction — "remove the filling
 * count focus, and move to a Texas focus, the data is coming from the full Cotality api set"):
 * the prototype's copy leaned on a specific "Collin County live, 35,000+ parcels" framing. That
 * framing is REMOVED here, not replaced with a different specific number — per the project's
 * reference-only-tier rule, this session has no ratified coverage figure to assert in its place,
 * and the whole point of the ask is to stop centering a county-by-county parcel count. Copy below
 * says "Texas-focused" and names the two real, already-wired data layers (county appraisal
 * records + Cotality) without a specific count. If a real, ratified statewide coverage figure
 * exists later, it is a one-line copy change here — flagged, not asserted now.
 *
 * LOGO (Session 8, board doc `06_OWNER_DIRECTION…` §5 "REAL GAP FOUND"): this file previously
 * rendered "ValuBrief" as plain text with a CSS dot standing in for a mark. That was a real gap —
 * the actual brand SVG (`brand/valubrief-logo.svg`, the same file `src/server/web/assets/` already
 * serves for the legacy PDF/HTML surface) now renders here and in the footer instead.
 */

/**
 * The width at or above which the whole nav fits on one line, in px. MUST match the `min-width`
 * in landing.css's `.nav` block — the CSS decides what is painted, this constant decides when the
 * open panel force-closes, and if the two disagree the panel can be left open and invisible with
 * its links still in the tab order.
 *
 * 1080 is measured, not guessed. Signed out, the bar carries six items — three section links plus
 * "Request access", "Open the app" and "Sign in". Rendered at their natural widths with the 24px
 * gaps and the 60px mark, the row needs ~1030px inside the gutter; the first width at which every
 * label still sits on one line is just above 1060. Below that the flex items shrank instead of
 * overflowing (no `flex-wrap`, no `white-space: nowrap`), so each label broke mid-phrase — "How
 * the analysis is / built", "Sign / in" — which is the defect the owner reported on 2026-08-10:
 * "part of it is designed for a mobile browser or app and some parts are not."
 */
const NAV_INLINE_MIN_PX = 1080;

/**
 * THE LANDING NAV, WITH A DISCLOSURE MENU BELOW 1080px.
 *
 * WHY A MENU AND NOT JUST HIDING THINGS. landing.css already had `@media (max-width: 760px)`
 * hiding the section links outright, which meant that between 760 and 1080 the bar was mangled,
 * and below 760 three real destinations simply vanished with nothing offering them. Raising that
 * threshold alone would have widened the band where the page silently loses navigation. Owner's
 * decision, 2026-08-10: hamburger.
 *
 * WHAT MOVES AND WHAT STAYS. Every item is rendered EXACTLY ONCE, in one place or the other —
 * never in both with CSS hiding a copy. Duplicated links are two tab stops, two screen-reader
 * announcements and two things to keep in sync, and this file has already been bitten once by two
 * surfaces disagreeing about the same fact (see the AUTH-AWARE NAV note below). So the bar keeps
 * the single primary action and the menu takes the rest:
 *
 *   signed out  bar: [mark] [Request access] [☰]   menu: sections, Open the app, Sign in
 *   signed in   bar: [mark] [Open the app]   [☰]   menu: sections, the signed-in address
 *   unknown     bar: [mark] [Open the app]   [☰]   menu: sections
 *
 * The primary action therefore never moves behind a click, at any width — which is the one thing
 * a hamburger is genuinely bad at, and the reason the pattern gets criticised.
 *
 * IT IS A DISCLOSURE, NOT A MODAL. No focus trap and no scroll lock: the panel is a short list
 * anchored under the bar, not a dialog, and trapping focus in it would be a heavier promise than
 * the UI keeps. Escape closes and returns focus to the button; a click outside closes; choosing a
 * link closes (the links are same-page anchors, so without that the panel would sit open over the
 * section it just scrolled to).
 */
/**
 * Is the bar wide enough to carry every item on one line?
 *
 * This drives WHAT IS RENDERED, not merely what is painted, so that no link exists twice in the
 * DOM. The alternative — render both arrangements and let CSS hide one — puts every secondary
 * destination in the document twice: two tab stops, two things to keep in sync. This file has
 * already been bitten once by two surfaces disagreeing about the same fact (the auth-aware note
 * in `Nav`), so it renders each item exactly once and lets JS decide where.
 *
 * `matchMedia` is guarded the same way `HeroCanvas.engine.ts` guards its own two queries: absent
 * (jsdom without the setupTests stub, or any non-browser render) resolves to the INLINE layout,
 * because a nav with everything visible degrades to "a bit crowded" while the collapsed layout
 * degrades to "the section links are behind a button that needs JS to open".
 */
function useNavIsInline(): boolean {
  const query = `(min-width: ${NAV_INLINE_MIN_PX}px)`;
  const [inline, setInline] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : true,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setInline(e.matches);
    setInline(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return inline;
}

export function Nav() {
  const session = useSession();
  const inline = useNavIsInline();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const navRef = useRef<HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);

  // Widening past the breakpoint closes the panel. Without this, opening at 900px and then
  // widening leaves `open` true, so re-narrowing later reveals a panel the visitor never asked
  // for a second time.
  useEffect(() => {
    if (inline) setOpen(false);
  }, [inline]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      // Escape without this leaves focus on a node that just became display:none, and the browser
      // drops it to <body> — the next Tab restarts from the top of the document.
      buttonRef.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  {/* `/signup` has no screen in `/next` yet; account creation still lives on the apex app, so the
      CTA points there rather than at a dead in-app path. `/login` is real (see
      screens/LoginScreen) and must be reachable from the nav, not only from the hero. */}
  {/* AUTH-AWARE NAV (2026-08-01). This used to render "Request access", "Open the app" and "Sign
      in" ALL AT ONCE, permanently — so a signed-in member arriving at their own product's home
      page was asked to request access to it, and given two competing ways in. Owner's report:
      "login or request access shows even when logged in, its very confusing."

      While `status` is "unknown" (first paint, before /api/me answers) only the neutral "Open the
      app" renders. Showing "Sign in" during that window is what makes an auth-aware header flash
      the wrong verb at someone who is already signed in. */}
  const signedOut = session.status === "out";
  const primaryIsRequestAccess = session.status !== "in";

  // The three same-page section anchors, and the secondary actions. Both lists are built once and
  // then placed EITHER in the bar (inline) OR in the panel (collapsed) — never both.
  const sectionLinks = (
    <>
      <a href="#what" onClick={close}>
        How the analysis is built
      </a>
      <a href="#how" onClick={close}>
        Process
      </a>
      <a href="#who" onClick={close}>
        Who it&apos;s for
      </a>
    </>
  );
  const secondaryActions = (
    <>
      {/* ONE WAY IN (2026-10-28, clean-pass audit §1). The bar used to offer "Open the app" AND
          "Sign in" side by side, which are the same destination for a visitor with an account. A
          signed-out visitor now sees "Request access" (the primary) and a single quiet "Sign in".
          While /api/me is still unanswered nothing here renders: a verb shown too early is the
          flash-of-wrong-state defect the notes above describe. */}
      {signedOut && (
        <a className="btn quiet" href="/login" onClick={close}>
          Sign in
        </a>
      )}
      {session.status === "in" && session.email && (
        <span className="nav-who mono">{session.email}</span>
      )}
    </>
  );

  return (
    <nav className="nav" ref={navRef}>
      <div className="nav-in">
        {/* THE MARK, IN BOTH SCHEMES (A-MARKETING, 2026-09-01). This was
            `<img src={valubrief-logo.svg} className="mark-logo">` — the navy lockup, which the
            sticky bar's own note says renders on "the light paper background the logo's navy/green
            palette was designed for". Once `--vb-brand-paper` flips to #0E1512 that sentence stops
            being true and the mark is not dim, it is absent: `ui/BrandMark.tsx` measures the two
            darkest wordmark steps at 1.35:1 and 1.11:1 on a dark bar.

            `BrandMark` shipped tonight with a derived dark lockup and the three-block cascade rule
            that shows exactly one of the two. Reusing it rather than repeating the switch here is
            the point of it existing — and it keeps the accessible name ("ValuBrief" on the light
            img, the dark one aria-hidden), which `Nav.test.tsx` reads.

            THE HEIGHT COMES BACK OUT OF CSS. `BrandMark` writes `height` as an inline style, which
            would beat the 44px phone rule in landing.css; the `style` prop is spread AFTER it, so
            handing it a custom property puts the media query back in charge. `--vb-mark-h` is
            declared on `.mark` / `.foot-mark` — the 60 and 48 below are the fallbacks a reader of
            this line would otherwise have to go and look up. */}
        <a className="mark" href="#top" onClick={close}>
          <BrandMark height={60} style={{ height: "var(--vb-mark-h, 60px)", width: "auto" }} />
        </a>

        {inline && <div className="nav-sections">{sectionLinks}</div>}

        <div className="nav-actions">
          {/* The primary action stays in the bar at EVERY width. Putting the one thing a visitor
              came to click behind a menu button is the failure mode hamburgers are rightly
              criticised for, and this page has exactly one primary action to protect. */}
          {primaryIsRequestAccess ? (
            <RequestAccessLink />
          ) : (
            <a className="btn ghost" href="/app">
              Open the app
            </a>
          )}

          {inline && <div className="nav-secondary">{secondaryActions}</div>}

          {!inline && (
            <button
              type="button"
              className="nav-toggle"
              ref={buttonRef}
              aria-expanded={open}
              aria-controls={menuId}
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
            >
              {/* Three bars drawn in CSS rather than an icon font or an inline SVG: the mark is
                  the only image this bar loads, and a decorative glyph should not add a second. */}
              <span className="nav-toggle-bars" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {!inline && (
        <div className="nav-panel" id={menuId} hidden={!open}>
          {sectionLinks}
          {secondaryActions}
        </div>
      )}
    </nav>
  );
}

/**
 * THE STRIP IS THE PROVENANCE VOCABULARY, AND NOTHING ELSE (A-MARKETING, 2026-09-01).
 *
 * WHAT CAME OUT. Two commercial terms — "No contract · no auto-renewal" — and, from the hero note
 * directly above, "Free during the pilot — no card". Every dollar and every commercial commitment
 * in this project's knowledge is reference-only and unratified; the site was making a contract
 * claim it could not point at a file for. There is no billing surface in this client to read one
 * out of. So the terms are gone rather than softened, and the strip now carries only the five
 * provenance tiers.
 *
 * THE FIVE ARE COPIED FROM `design-tokens.ts` `tierStyles`, in its order, in its words: verified
 * -> "Sourced", indicative -> "Indicative", assumption -> "Assumption", entered -> "You entered",
 * gap -> "Not found". If that record changes, this line is wrong and should change with it.
 */
export function TrustStrip() {
  return (
    <div className="strip">
      <div className="strip-in mono">
        <span>Five provenance tiers</span>
        <span>Sourced</span>
        <span>Indicative</span>
        <span>Assumption</span>
        <span>You entered</span>
        <span>Not found</span>
        <span>Every figure carries one</span>
      </div>
    </div>
  );
}

/**
 * THE EXAMPLE MARKER ON THE CARD FOOTERS (A-MARKETING, 2026-09-01).
 *
 * The three cards each end in a small monospace block that looks exactly like a row lifted out of
 * a finished report — an address, a dollar figure, a tier. Those addresses and figures are
 * invented, and until tonight nothing on the page said so. On a page whose entire subject is that
 * a number without a source may not influence anything, an unlabelled specimen figure is the one
 * mistake the product exists to oppose.
 *
 * They are not deleted, because the specimen is what makes the tier vocabulary legible to someone
 * who has never seen the product. They are labelled instead, in the block itself, above the
 * figures rather than below them.
 */
function FootExample({ children }: { children: React.ReactNode }) {
  return (
    <div className="foot">
      <span className="foot-eg">Example — invented for this page</span>
      {children}
    </div>
  );
}

/**
 * HOW A NUMBER GETS ITS SOURCE, IN PLAIN WORDS (2026-10-28, landing polish).
 *
 * The page's one claim is "every number traces to its source", and until now the explanation sat
 * inside a card footer. This section says it in three plain lines. Nothing here is new: the three
 * tiers are the ones `TrustStrip` already prints from `design-tokens.ts` `tierStyles`, and the
 * sentences restate the "Five-tier provenance" card and the report's "What you overrode, and why"
 * section. No figure on this page is real data, so the specimen row carries the same
 * "Example — invented for this page" label the card footers use.
 */
export function SectionTrace() {
  return (
    <section className="trace-sec" id="trace">
      <div className="wrap">
        <div className="sec-head">
          <span className="mono">Source-traceable</span>
          <h2>Every number says where it came from.</h2>
          <p>Next to each figure in a report you will see one of three things.</p>
        </div>
        <div className="trace">
          <div className="trace-item">
            <span className="trace-tag mono">Sourced</span>
            <p>
              The name of the record it was read from, such as a county appraisal record or the file you
              uploaded.
            </p>
          </div>
          <div className="trace-item">
            <span className="trace-tag mono">You entered</span>
            <p>You typed it. You can edit it in place, and a change you make is listed with your reason.</p>
          </div>
          <div className="trace-item">
            <span className="trace-tag mono">Not found</span>
            <p>The source did not have it. The field shows a dash and stays blank. Nothing is filled in for you.</p>
          </div>
        </div>
        <div className="foot trace-eg">
          <span className="foot-eg">Example — invented for this page</span>
          EST. SALE PRICE <b>$335,000</b> · SOURCED
          <br />
          SELLER CONCESSIONS <b>—</b> · NOT FOUND
        </div>
      </div>
    </section>
  );
}

export function SectionCards() {
  return (
    <section id="what">
      <div className="wrap">
        <div className="sec-head">
          <span className="mono">How the analysis is built</span>
          <h2>The engine does the arithmetic. The model writes the sentences. Neither one invents a figure.</h2>
          <p>
            Three properties hold in every vertical, and all three are about what the software is{" "}
            <b>not</b> allowed to do.
          </p>
        </div>
        <div className="cards">
          <article className="card">
            <span className="tag">Deterministic math</span>
            <h3>The model never produces a number</h3>
            <p>
              The engine computes every dollar from the evidence. The model classifies condition and writes the
              prose — it never emits a figure, so there is nothing for it to hallucinate.
            </p>
            <div className="foot">
              LABELS IN · <b>DOLLARS OUT</b>
            </div>
          </article>
          <article className="card">
            <span className="tag">Disclosed, not buried</span>
            <h3>A weak comparable is marked where it sits</h3>
            <p>
              A thin or stretched comparable is flagged on the page it appears on, not smoothed over. A disclosed
              gap is the honest result; a fabricated figure is the only real failure.
            </p>
            <FootExample>
              CEDAR CT · SAME STREET <b>$375,000</b>
              <br />
              OAK LN · SOLD AS-IS <b>$310,000 ⚑</b>
            </FootExample>
          </article>
          <article className="card">
            <span className="tag">Five-tier provenance</span>
            <h3>Click any number, see its source</h3>
            <p>
              Every figure carries its origin — a named record, your own entry, or an explicit &quot;Not found&quot;
              when the source file did not contain it. A blank stays blank. A figure with no source is barred
              from influencing the result.
            </p>
            <FootExample>
              EST. SALE PRICE <b>$335,000</b> · SOURCED
              <br />
              SELLER CONCESSIONS <b>—</b> · NOT FOUND
            </FootExample>
          </article>
        </div>
      </div>
    </section>
  );
}

export function SectionSteps() {
  return (
    <section className="steps-sec" id="how">
      <div className="wrap">
        <div className="sec-head">
          <span className="mono">Process</span>
          <h2>Three steps, every figure tagged.</h2>
        </div>
        <div className="steps">
          <div className="step">
            <span className="n">01</span>
            <h3>Start with your file</h3>
            <p>
              {/* UI-029: there is no search front door in the app yet — do not promise one. */}
              Upload the subject&apos;s county or MLS sheet along with your comparables — Excel, CSV, PDF, or an MLS
              export. Where we have your county&apos;s appraisal records loaded, we fill the gaps from them.
            </p>
          </div>
          <div className="step">
            <span className="n">02</span>
            <h3>Review what we found</h3>
            {/* UI-029: don't imply that clicking past an app-supplied default is a human confirmation. */}
            <p>
              Guided review shows you the subject and every comparable before anything generates — edit anything
              that is wrong. Nothing is invented to fill a gap.
            </p>
          </div>
          <div className="step">
            <span className="n">03</span>
            <h3>Deliver under your license</h3>
            {/* A-MARKETING 2026-09-01: "Firm branding ... is coming to the new experience" was a roadmap
                promise. `/branding` is a real, auth-guarded route in this client (App.tsx) — so the
                sentence names the screen that exists and stops there. The formats are the two the
                report screen actually offers: `FMT_LABEL` in cma/NarrativeReportScreen.tsx is
                `{ pdf: "PDF", docx: "Word file" }`, alongside the in-app preview. */}
            <p>
              A client-ready deliverable — read it in the app, or download it as a PDF or a Word file, under your
              license. Your firm&apos;s mark and details are set once on the branding screen.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


/**
 * THE CHIP IS DERIVED, NOT WRITTEN (2026-09-04).
 *
 * A hand-typed "Coming soon" is a second copy of a fact that already lives in `flags.ts`, and a
 * second copy is a thing that goes stale silently — the flag flips, the page keeps saying the old
 * word, and the page is now lying about the one subject it exists to be trusted on. So the chip
 * reads the flag. Flip `APPRAISAL_SUPPORT_ENABLED` and this pane relabels itself.
 *
 * Protest has no flag to read because it has no route to gate — there is no protest screen in
 * App.tsx at all — so `false` here is the honest constant rather than a placeholder. The day a
 * route exists, it gets a flag and this becomes a read like its neighbour.
 */
/* `PROTEST_LIVE` and the two words now live in `../verticalStatus.ts` (2026-09-25), so the New brief
   dialog reads the same fact in the same words instead of keeping a second copy. */
function Status({ live }: { live: boolean }) {
  return (
    <span className="status" data-live={live ? "yes" : "no"}>
      {statusWord(live)}
    </span>
  );
}

/**
 * WHO IT'S FOR — LEADING WITH THE READER, LANDING ON THE VERTICAL.
 * (2026-09-04, owner decision. Was "Three verticals, equal billing" from 2026-09-01, which was
 * itself "Two practices, one source-traced engine" before that.)
 *
 * WHY IT CHANGED AGAIN, AND WHAT DID NOT CHANGE. The three co-equal verticals are a locked
 * decision and they are still exactly what this section is about — protest, BPO, appraisal
 * support, three panes of the same width, no favourite. What changed is the AXIS THE READER
 * ENTERS BY. The 09-01 version organised by the ENGINE's truth: three modes over one engine.
 * That is correct and it is how the code is built, but nobody arrives at this page thinking "I
 * need the Broker's Price Opinion vertical". They arrive thinking "I am a tax consultant in
 * Collin County". So each pane now opens with the reader and names the vertical as what they GET.
 * The architecture is unchanged; the door is on the other side of it.
 *
 * THIS ALSO SETTLES A CONTRADICTION THE CHARTER CARRIED. `CLAUDE.md` names five personas while
 * this section named three verticals, and the page had to pick one — so the two documents said
 * different things about the same product. Persona-in, vertical-out is both at once, which is why
 * the section id is `who` again rather than in spite of it: the id predates the 09-01 rename and
 * was the only thing on the page still saying what this section is.
 *
 * COMMERCIAL AND RESIDENTIAL, SAID PLAINLY (owner decision, same date). The app's own intake still
 * says "the property you're valuing" rather than "the home", and this section says both out loud,
 * because the page previously implied residential by omission while the company is positioned as
 * commercial. Stating it is cheaper than either audience guessing wrong.
 *
 * STATUS CHIPS, AND WHY THEY DO NOT BREAK "CO-EQUAL" (2026-09-04, owner). Two of the three panes
 * now read "Coming soon" and one reads "Live now". That is not a demotion of two verticals and it
 * is not the page picking a favourite: the three are still the architecture, still the same width,
 * still the same treatment. It is a statement about AVAILABILITY, which is a different fact from
 * billing, and it is checked against `flags.ts` and App.tsx's route table rather than against
 * anyone's intention. (As of 2026-09-25 `APPRAISAL_SUPPORT_ENABLED` is true, so appraisal reads "Live
 * now"; no protest route exists. All of it is read from `verticalStatus.ts`.)
 *
 * The availability note has always told this truth, but it sat at the FOOT of the section, after
 * three columns of detail, so a visitor read three equal offers and met the caveat last or never.
 * A product whose whole claim is that it discloses rather than smooths cannot bury its own
 * shipping status below the fold.
 *
 * THE BULLETS ARE VERBATIM FROM THE 09-01 PASS AND THAT IS DELIBERATE. Every one of them is a
 * legal or standards claim traced to a source file and a citation (see the map below), and two
 * claims were REMOVED from this section once already for being unsourced. Density was the
 * complaint that prompted this rework, and the fix is a one-line deliverable summary that lets a
 * scanner stop reading — not shorter bullets. Trimming a sourced sentence to save a line is how a
 * precise claim quietly becomes an overstatement.
 *
 * WHAT WAS WRONG. The page was a residential CMA page with commercial work listed underneath it:
 * the section head sold the CMA, the first pane mixed homes and commercial into one column, and
 * property tax got the other. The product is one engine under three co-equal verticals — protest,
 * BPO, appraisal support — so it gets three columns of the same width.
 *
 * WHAT EACH BULLET IS POINTING AT. Every line below is a description of something in
 * `src/appraisal/`, documented with a source and a retrieval date in `docs/APPRAISAL-MODULE.md`:
 *   protest   -> equal_and_uniform.py (Tex. Tax Code 41.43(b)(3), 42.26), texas_statute.py (23.013
 *                recency gate, eleven factors listed and unweighted)
 *   BPO       -> bpo_guard.py (Tex. Occ. Code 1103.004(2), three conditions, fails closed)
 *   appraisal -> sales_grid.py (net/gross/adjusted, Fannie Mae SG B4-1.3-09), ratio_study.py
 *                (COD/PRD, IAAO 2013), parked.py (#3/#4/#5 — no adjustment is auto-derived)
 *
 * WHAT WAS REMOVED. "USPAP-aware appraisal-support workfiles". `src/appraisal/uspap.py` reproduces
 * no USPAP rule text — the edition is sold rather than published and was not read — and a
 * package-wide AST sweep in the suite fails the build if a Standards Rule citation appears in a
 * string constant. A landing page calling the product "USPAP-aware" is the same claim by another
 * route, made about text nobody here has read.
 *
 * AND "Win the equity argument" is gone: it promised an outcome in a hearing. The software
 * computes a median and cites the section it came from; it does not win anything.
 *
 * THE AVAILABILITY NOTE AT THE FOOT IS NOT A HEDGE, IT IS THE PRODUCT'S OWN RULE APPLIED TO
 * ITSELF. `client/src/App.tsx` serves `/cma`, `/branding`, `/briefs` and `/crm` outright; the
 * appraisal workspace is routed at `/appraisal` behind `APPRAISAL_SUPPORT_ENABLED`. Listing three
 * verticals above that line and saying nothing would tell a visitor they can open all three today.
 * They cannot. The flag flipped on and this sentence did not follow it, so since 2026-09-25 it reads
 * `verticalStatus.ts` instead of restating the flag in prose.
 */
export function SectionPractices() {
  return (
    <section id="who">
      <div className="wrap">
        <div className="sec-head">
          <span className="mono">Who it&apos;s for</span>
          <h2>Find your own work here. What runs underneath does not change.</h2>
          <p>
            Commercial and residential both — the engine reads a parcel the same way either way. The same
            provenance model and the same refusals run under all three kinds of work. What changes is the
            deliverable, and the record it has to stand up in front of.
          </p>
        </div>
        <div className="split three">
          <div className="pane">
            <div className="pane-top">
              <span className="who">Property-tax consultants &amp; attorneys</span>
              <Status live={PROTEST_LIVE} />
            </div>
            <h3>The median, and the section it comes from.</h3>
            <p className="deliverable">
              You get a <b>property-tax protest</b> package.
            </p>
            <ul>
              <li>
                Equal-and-uniform computed as the median of the comparables you include, each carrying your own
                adjustments — with the statute section and the date it was read printed beside it
              </li>
              <li>
                A sale-recency gate that fails closed: when the property kind or the county population is
                missing, the answer is <b>indeterminate</b> and it names the missing fact rather than defaulting
              </li>
              <li>
                The statutory comparability factors are listed, not scored. No weighting is applied, because
                none is published
              </li>
            </ul>
          </div>
          <div className="pane">
            <div className="pane-top">
              <span className="who">Brokers, agents &amp; brokerages</span>
              <Status live={VERTICAL_LIVE.bpo} />
            </div>
            <h3>Labelled as what it is.</h3>
            <p className="deliverable">
              You get a <b>Broker&apos;s Price Opinion</b> — or a CMA or Advanced Market Brief.
            </p>
            <ul>
              <li>
                The Texas broker-exemption conditions are checked mechanically and the check fails closed — a
                context field you did not supply is a defect, never a pass
              </li>
              <li>
                The word <i>appraisal</i> is barred from six label classes: the deliverable name, the filename,
                the page header, the PDF title, the export menu and the email subject
              </li>
              <li>
                ValuBrief will not label a BPO as satisfying a lender&apos;s evaluation requirement. That one is
                not on a roadmap — it never ships
              </li>
            </ul>
          </div>
          <div className="pane">
            <div className="pane-top">
              <span className="who">Appraisers</span>
              <Status live={VERTICAL_LIVE.appraisal_support} />
            </div>
            <h3>Evidence, never an opinion.</h3>
            <p className="deliverable">
              You get <b>appraisal support</b>: a workfile of evidence, not a value.
            </p>
            <ul>
              <li>
                A sales-comparison grid: net and gross adjustment totals and adjusted prices, computed from the
                adjustments you enter, with no pass or fail applied to the totals
              </li>
              <li>
                Ratio-study diagnostics read against published ranges the standards body itself calls advisory —
                and a figure outside a range is described in words, never in colour alone
              </li>
              <li>
                No adjustment is derived for you — not by paired sales, not by regression, not by extraction.
                The field reads <b>— adjustment not derived</b> and says what would change that
              </li>
            </ul>
          </div>
        </div>
        <p className="avail">
          {/* READ FROM THE SAME SOURCE AS THE CHIPS ABOVE (2026-09-25). This sentence said "Coming —
              appraisal support … behind a flag that is off" for weeks after the flag went on, while the
              appraisal chip above it said "Live now": the page contradicted itself. It now reads
              `verticalStatus.ts`, which the chips and the New brief dialog read too. */}
          {VERTICAL_LIVE.appraisal_support ? (
            <>
              <b>Live now</b> — the CMA and Advanced Market Brief flow, appraisal support, and the CRM your
              records live in.{" "}
              <b>Coming</b> — property-tax protest and the commercial workflow. Their analysis modules are built,
              cited and under test; the screens that show them are not in the app yet, so you cannot reach them.
            </>
          ) : (
            <>
              <b>Live now</b> — the CMA and Advanced Market Brief flow, and the CRM your records live in.{" "}
              <b>Coming</b> — property-tax protest, appraisal support, and the commercial workflow. Their analysis
              modules are built, cited and under test; the screens that show them are not in the app yet, so you
              cannot reach them.
            </>
          )}{" "}
          The engine will read a commercial parcel today — what is not
          finished is the workflow built around it. We would rather print all of that here than let the page
          imply otherwise. Statute and guideline references carry their source and the date it was read;
          whether any of it applies to your property is a question for your attorney, not for this software.
        </p>
      </div>
    </section>
  );
}

/**
 * THE CLOSING BAND — AND THE 1300px OF FLAT GREEN (A-MARKETING, 2026-09-01).
 *
 * MEASURED, NOT ARGUED. In the 1920 screenshot taken before this change
 * (`docs/runs/evidence/w3-marketing/before/landing@1920.png`), the band is full-bleed 1920px wide
 * and every word in it sits inside a 520px column — `.close h2` is capped at 13.49em and
 * `.close p` at 31.54em. So roughly 1400px of the band carried nothing at all, and the right half
 * of the composition read as a printing error.
 *
 * A PREVIOUS AGENT DECLINED TO TOUCH IT, reasoning that `landing.css` records those measures as
 * deliberate. That reasoning is half right, and the half it gets right is the half that survives:
 * the MEASURE is deliberate and correct — 13.49em is the line length the 700-weight heading was
 * typeset for, and the note above it in landing.css shows the number was derived from Inter's own
 * advance widths rather than guessed. Widening the column would be a real regression.
 *
 * But a measure is a statement about a line of type. It is not a statement about what the other
 * 1400px of a full-bleed band should contain, and nothing in the file ever made that second claim.
 * So the measure is kept to the pixel and the band is given a second column instead: the refusals
 * below, which had no home on the page and are the strongest thing this product has to say. At
 * 1100px and under it stacks and the band is exactly what it was.
 *
 * EVERY REFUSAL HERE IS A ROW IN `docs/APPRAISAL-MODULE.md` §3 ("The parked features"), which
 * lists thirteen and names, for each, the document that would unpark it. Two of the thirteen are
 * recorded there as never shipping at all; both are on this list.
 */
const REFUSALS: { head: string; body: string }[] = [
  {
    head: "Derive an adjustment for you",
    body: "Not by paired sales, not by regression, not by extraction. No authority publishes a method, and a plausible reconstruction would be indistinguishable from a standard.",
  },
  {
    head: "Infer a condition or quality rating",
    body: "Not from a photograph, a description or an age. The field stays empty and says so.",
  },
  {
    head: "Reproduce USPAP rule text",
    body: "The current edition is sold rather than published and has not been read here. A test fails the build if a Standards Rule citation appears in the code at all.",
  },
  {
    head: "Call a BPO a lender's evaluation",
    body: "The federal guidelines say in terms that it is not one. This one is not parked pending a document — it never ships.",
  },
  {
    head: "Pick your comparables out of sight",
    body: "A set may be suggested. It stays listed and editable before any figure is computed from it.",
  },
];

export function CtaBand() {
  return (
    <section className="close">
      <div className="wrap close-in">
        <div className="close-copy">
          {/* A-MARKETING 2026-09-01: the eyebrow read "Pilot — free, no payment required". That is a
              price, and this project has no ratified one; `RequestAccessLink` is the only thing on
              the page that knows anything about access, and what it knows is that sign-ups are
              closed and the button goes to /login. So the eyebrow says that and nothing more.
              UI-021's colour ruling stands: --vb-brand-band-* only, never a literal. */}
          <span className="mono close-eyebrow">Access is by request</span>
          <h2>Built for the reader who checks.</h2>
          <p>
            Every figure resolves to a named source or to the words <i>you entered</i>. A licensed professional
            reviews it, signs it and files it.
          </p>
          <RequestAccessLink />
          <p className="fineprint">
            Source-traceable analysis. Not an appraisal, not legal or tax advice. ValuBrief supports the licensed
            professional who produces and owns the opinion — it never states an opinion of value or represents
            itself as an appraiser.
          </p>
        </div>
        <div className="close-refuse">
          <h3 className="mono">What it will not do</h3>
          <ul>
            {REFUSALS.map((r) => (
              <li key={r.head}>
                <b>{r.head}</b>
                <span>{r.body}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer>
      <div className="foot-in">
        <span className="foot-mark">
          <BrandMark height={48} style={{ height: "var(--vb-mark-h, 48px)", width: "auto" }} />
          <span>© 2026 ValuBrief</span>
        </span>
        <span className="right">
          <a href="#who">Property tax</a>
          <a href="#who">BPO</a>
          <a href="#who">Appraisal support</a>
          <a href="mailto:hello@valubrief.ai">Contact</a>
        </span>
      </div>
    </footer>
  );
}
